import React, {useEffect, useMemo, useState} from "react";
import {FormikProps, useFormik} from "formik";
import * as Yup from "yup";
import {useIntl, FormattedMessage} from "react-intl";

import {FormInput, Forms, GradientButton, notify, VerificationPinModal} from "@housebets/housebets-x-package";

import {PinForm} from "app/components/features/forms";

import {useAppSelector} from "hooks/useAppSelector";
import {updateUserInfo} from "app/store/slices/user";
import apiUser from "app/api/user";
import {IEmailVerificationProps, TFormikConfig} from "./types";
import {emailSchema} from "app/utils/schemaValidation";
import { useDispatch } from "react-redux";

const initData: TFormikConfig = {
    email: "",
};

const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
    formik.setValues({...initData});
    formik.setErrors({});
    formik.setTouched({});
    setError?.(false);
};

const formSchema = () =>
    Yup.object().shape({
        email: emailSchema,
    });

export const EmailVerificationForm: React.FC<IEmailVerificationProps> = ({onSubmit = () => {}}) => {
    const {$t} = useIntl();
    const token = useAppSelector((state) => state.user.token);
    const dispatch = useDispatch();

    const [inputEmail, setInputEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isverifyIng, setIsverifyIng] = useState(false);

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {...initData},
        validationSchema: formSchema(),
        validateOnBlur: true,
        onSubmit: async (data) => {
            const email = data.email || "";
            setInputEmail(email);
            // setIsModalOpen(true);

            sendVerifyCode(email);
        },
    });

    const sendVerifyCode = async (email?: string) => {
        setIsLoading(true);
        try {
            const {status, data} = await apiUser.userSendVerifyEmail(email || inputEmail);
            if (status === 200 && data.code === 0) {
                setIsModalOpen(true);
                notify({
                    type: "success",
                    title: $t({id: "CodeSendSuccess"}),
                });
                return;
            }
            notify({
                type: "error",
                title: $t({id: `VerifyPhoneFail${data.code}`}),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const submitVerifyCode = async (pin: string | undefined) => {
        try {
            setIsverifyIng(true);
            const {status, data} = await apiUser.userVerifyEmailByCode(inputEmail, pin || "");
            if (status === 200 && data.code === 0) {
                dispatch(updateUserInfo({...data.data, token}));
                setIsModalOpen(false);
                initForm(formik);
                onSubmit();
                return;
            }

            notify({
                type: "error",
                title: $t({id: `BindEmailError_${data.code}`}),
            });
        } finally {
            setIsverifyIng(false);
        }
    };

    const config = useMemo(() => {
        return {
            title: "We sent the code to the email:",
            value: inputEmail,
            submitButtonText: "Confirm code",
            requestCodeButtonText: "Request code again",
            isLoading: isverifyIng,
        };
    }, [inputEmail, isverifyIng]);

    useEffect(() => {
        initForm(formik);
    }, []);

    return (
        <>
            <Forms.ContactVerify.Wrapper>
                <Forms.ContactVerify.Form onSubmit={formik.handleSubmit}>
                    <Forms.ContactVerify.Row mail>
                        <p>
                            <FormattedMessage id="EmailVerificationForm.emailLabel" />
                        </p>

                        <FormInput
                            showIcon={false}
                            size="sm"
                            placeholder={$t({id: "EmailVerificationForm.emailPlaceholder"})}
                            name="email"
                            type="email"
                            required
                            value={formik.values?.email}
                            errors={
                                (!!formik.errors?.email && !!("email" in formik.touched)) || error
                                    ? {
                                          message: formik.errors?.email || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("email", val)}
                            onFocus={() => formik.setFieldTouched("email", false)}
                            onBlur={() => formik.setFieldTouched("email", true)}
                        />
                    </Forms.ContactVerify.Row>

                    <Forms.ContactVerify.Row>
                        <GradientButton
                            type="submit"
                            text={$t({id: "EmailVerificationForm.verifyButton"})}
                            kind="fill"
                            fullWidth
                            disabled={!formik.isValid || isLoading}
                        />
                    </Forms.ContactVerify.Row>
                </Forms.ContactVerify.Form>
            </Forms.ContactVerify.Wrapper>

            <VerificationPinModal title="Email confirmation" isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <PinForm
                    onRequestCode={sendVerifyCode}
                    modifyButtonText={$t({id: "EmailVerificationForm.modifyButtonText"})}
                    onModify={() => {
                        setIsModalOpen(false);
                    }}
                    onSubmit={(v) => submitVerifyCode(v?.pin)}
                    type="pin"
                    {...config}
                />
            </VerificationPinModal>
        </>
    );
};

EmailVerificationForm.displayName = "EmailVerificationForm";
